import React, { useEffect, useState } from "react";
import { getRequest, postRequest } from "../../utils/axios";
import { NotificationManager } from "react-notifications";

import MainA from "../../layout/Main Admin";
import Loader from "../../components/atoms/Loader";
import Button from "../../components/atoms/Button";
import Modal from "../../components/atoms/Modal";
import Table from "../../components/atoms/Table";
import ToggleSwitch from "../../components/atoms/ToggleSwitch";
import Input from "../../components/atoms/Input";

import plus from "../../assets/icons/plus.svg";

import { getCurrentUser } from "../../utils/utils";

import "./styles.scss";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCopy } from "@fortawesome/free-solid-svg-icons";

const Group = () => {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);

  const [newGroupName, setNewGroupName] = useState("");
  const [newGroupActive, setNewGroupActive] = useState(true);
  const [newGroupLocation, setNewGroupLocation] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const [editingGroupId, setEditingGroupId] = useState(null);
  const [showAdminModal, setShowAdminModal] = useState(false);
  const [selectedGroupForAdmin, setSelectedGroupForAdmin] = useState(null);
  const [groupAdmins, setGroupAdmins] = useState([]);
  const [newAdminEmail, setNewAdminEmail] = useState("");
  const [newAdminActive, setNewAdminActive] = useState(true);
  const [createdBy, setCreatedBy] = useState("Unknown");
  const [createdDate, setCreatedDate] = useState(new Date());
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [groupToDelete, setGroupToDelete] = useState(null);
  const [showEventsModal, setShowEventsModal] = useState(false);
  const [selectedGroupEvents, setSelectedGroupEvents] = useState([]);
  const [selectedGroupForEvents, setSelectedGroupForEvents] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [filteredGroups, setFilteredGroups] = useState([]);
  

  const baseUrl = "https://pikusports.com/form/";

  useEffect(() => {
    fetchGroups();
  }, []);

  // update filtered groups whenever the source data or query changes
  useEffect(() => {
    setFilteredGroups(filterGroups(searchQuery, groups));
  }, [searchQuery, groups]);

  // returns filtered and sorted groups based on query
  const filterGroups = (q, source) => {
    const query = String(q || "").trim().toLowerCase();
    const list = Array.isArray(source) ? source.slice() : [];
    if (!query) return list.sort((a, b) => new Date(b.createdDate) - new Date(a.createdDate));

    return list
      .filter((g) => {
        const name = String(g.groupName || "").toLowerCase();
        const location = String(g.groupLocation || "").toLowerCase();
        const admins = String(g.adminEmails || "").toLowerCase();
        return name.includes(query) || location.includes(query) || admins.includes(query);
      })
      .sort((a, b) => new Date(b.createdDate) - new Date(a.createdDate));
  };

  const fetchGroups = async () => {
    setLoading(true);
    try {
      const resp = await getRequest("Group/GetGroups");
      if (resp?.status === 200)
        setGroups(Array.isArray(resp.data) ? resp.data : []);
      else setGroups([]);
    } catch (err) {
      console.error(err);
      NotificationManager.error("Failed to fetch groups");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateGroup = async () => {
    if (!newGroupName || newGroupName.trim() === "") {
      NotificationManager.error("Please enter a group name");
      return;
    }

    try {
      const payload = {
        groupName: newGroupName.trim(),
        isActive: newGroupActive,
        groupLocation: newGroupLocation.trim(),
        createdBy: String(getCurrentUser()?.userId) || "Unknown",
      };

      const editingPayload = {
        groupId: editingGroupId,
        groupName: newGroupName.trim(),
        isActive: newGroupActive,
        groupLocation: newGroupLocation.trim(),
        createdBy: createdBy,
        createdDate: createdDate,
      };

      if (isEditing && editingGroupId !== null) {
        const resp = await postRequest("Group/UpdateGroup", editingPayload);
        if (resp?.status === 200 || resp?.status === 201) {
          NotificationManager.success("Group updated successfully");
          setShowModal(false);
          setIsEditing(false);
          setEditingGroupId(null);
          setNewGroupName("");
          setNewGroupActive(true);
          setNewGroupLocation("");
          fetchGroups();
        } else {
          NotificationManager.error(
            resp.response.data || "Failed to update group",
          );
        }
      } else {
        const resp = await postRequest("Group/CreateGroup", payload);
        if (resp?.status === 200 || resp?.status === 201) {
          NotificationManager.success("Group created successfully");
          setShowModal(false);
          setNewGroupName("");
          setNewGroupActive(true);
          setNewGroupLocation("");
          fetchGroups();
        } else {
          NotificationManager.error(
            resp.response.data || "Failed to create group",
          );
        }
      }
    } catch (err) {
      console.error(err);
      NotificationManager.error(
        isEditing ? "Failed to update group" : "Failed to create group",
      );
    }
  };

  const handleEditGroup = (group) => {
    if (!group) return;
    setIsEditing(true);
    setEditingGroupId(group.groupId);
    setNewGroupName(group.groupName || "");
    setNewGroupLocation(group.groupLocation || "");
    setNewGroupActive(!!group.isActive);
    setCreatedBy(group.createdBy || "Unknown");
    setCreatedDate(
      group.createdDate ? new Date(group.createdDate) : new Date(),
    );
    setShowModal(true);
  };

  const handleDeleteGroup = async () => {
    setLoading(true);
    try {
      const resp = await getRequest(
        `Group/DeleteGroup/${groupToDelete.groupId}`,
      );
      if (resp?.status === 200) {
        NotificationManager.success("Group deleted");
        setGroups((prev) =>
          prev.filter((g) => g.groupId !== groupToDelete.groupId),
        );
      } else {
        NotificationManager.error("Failed to delete group");
      }
    } catch (err) {
      console.error(err);
      NotificationManager.error("Failed to delete group");
    } finally {
      setDeleteModalOpen(false);
      setGroupToDelete(null);
      setLoading(false);
    }
  };

  const handleChangeStatus = async (group) => {
    if (!group || (!group.groupId && group.groupId !== 0)) return;

    const newStatus = !group.isActive;
    setLoading(true);
    try {
      const resp = await getRequest(
        `Group/ChangeStatus/${group.groupId}/${newStatus}`,
      );
      if (resp?.status === 200) {
        NotificationManager.success("Status updated");
        setGroups((prev) =>
          prev.map((g) =>
            g.groupId === group.groupId ? { ...g, isActive: newStatus } : g,
          ),
        );
      } else {
        NotificationManager.error("Failed to update status");
      }
    } catch (err) {
      console.error(err);
      NotificationManager.error("Failed to update status");
    } finally {
      setLoading(false);
    }
  };

  const fetchGroupAdmins = async (groupId) => {
    if (!groupId && groupId !== 0) return;
    setLoading(true);
    try {
      const resp = await getRequest(`GroupAdmin/GetByGroup/${groupId}`);
      if (resp?.status === 200)
        setGroupAdmins(Array.isArray(resp.data) ? resp.data : []);
      else setGroupAdmins([]);
    } catch (err) {
      console.error(err);
      NotificationManager.error("Failed to fetch group admins");
    } finally {
      setLoading(false);
    }
  };

  const handleOpenAdminModal = async (group) => {
    setSelectedGroupForAdmin(group);
    setShowAdminModal(true);
    await fetchGroupAdmins(group.groupId);
  };

  const handleCreateGroupAdmin = async () => {
    if (!selectedGroupForAdmin) return;
    if (!newAdminEmail || newAdminEmail.trim() === "") {
      NotificationManager.error("Please enter admin email");
      return;
    }

    try {
      const payload = {
        GroupAdminEmail: newAdminEmail,
        GroupId: selectedGroupForAdmin.groupId,
        IsActive: newAdminActive,
        CreatedBy: String(getCurrentUser()?.userId) || "Unknown",
      };

      const resp = await postRequest("GroupAdmin/CreateGroupAdmin", payload);
      if (resp?.status === 200 || resp?.status === 201) {
        NotificationManager.success("Group admin added");
        setNewAdminEmail("");
        setNewAdminActive(true);
        await fetchGroupAdmins(selectedGroupForAdmin.groupId);
        fetchGroups();
      } else {
        NotificationManager.error("Failed to add admin");
      }
    } catch (err) {
      console.error(err);
      NotificationManager.error("Failed to add admin");
    }
  };

  const handleToggleAdminStatus = async (admin) => {
    if (!admin || (!admin.groupAdminId && admin.groupAdminId !== 0)) return;
    const newStatus = !admin.isActive;
    setLoading(true);
    try {
      const resp = await getRequest(
        `GroupAdmin/ChangeStatus/${admin.groupAdminId}/${newStatus}`,
      );
      if (resp?.status === 200) {
        NotificationManager.success("Admin status updated");
        setGroupAdmins((prev) =>
          prev.map((a) =>
            a.groupAdminId === admin.groupAdminId
              ? { ...a, isActive: newStatus }
              : a,
          ),
        );
        fetchGroups();
      } else {
        NotificationManager.error("Failed to update admin status");
      }
    } catch (err) {
      console.error(err);
      NotificationManager.error("Failed to update admin status");
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmDeleteAdmin = async (admin) => {
    if (!admin) return;
    setLoading(true);
    try {
      const resp = await getRequest(`GroupAdmin/DeleteGroupAdmin/${admin.groupAdminId}`);
      if (resp?.status === 200) {
        NotificationManager.success('Group admin removed');
        setGroupAdmins((prev) => prev.filter((a) => a.groupAdminId !== admin.groupAdminId));
        fetchGroups();
      } else {
        NotificationManager.error('Failed to remove admin');
      }
    } catch (err) {
      console.error(err);
      NotificationManager.error('Failed to remove admin');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = (e, group) => {
    e.preventDefault();
    setGroupToDelete(group);
    setDeleteModalOpen(true);
  };

  const closeDeleteModal = () => {
    setDeleteModalOpen(false);
    setGroupToDelete(null);
  };

  return (
    <MainA className="b-group-wrapper">
      <Loader loading={loading} />
      <div className="wrapper">
        <div className="head">
          <h4>Groups</h4>
          <div className="view-toggle">
            <div className="head-search" style={{ margin: "8px 0" }}>
              <Input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search..."
              />
            </div>
            <Button onClick={() => setShowModal(true)}>
              <img src={plus} alt="add" /> Add Group
            </Button>
          </div>
        </div>

        <div className="group-table">
          <Table pagination={true} pageSize={10} totalRows={filteredGroups?.length} categoryName="groups">
            <thead>
              <tr>
                <th>Group Name</th>
                <th>Group Location</th>
                <th>Active</th>
                {/* <th style={{ textAlign: "center" }}>Event Count</th> */}
                <th>Admins</th>
                <th>Url</th>
                <th style={{ textAlign: "right" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredGroups && filteredGroups.length > 0 ? (
                filteredGroups.map((g) => (
                  <tr key={g.groupId}>
                    <td>{g.groupName || "-"}</td>
                    <td>{g.groupLocation || "-"}</td>
                    <td>
                      <ToggleSwitch
                        checked={!!g.isActive}
                        onChange={() => handleChangeStatus(g)}
                      />
                    </td>
                    {/* <td
                      style={{
                        textAlign: "center",
                        position: "relative",
                        color:
                          Array.isArray(g.events) && g.events.length > 0
                            ? "#0000EE"
                            : "inherit",
                      }}
                    >
                      {g.events.length > 0 ? (
                        <div
                          className="event-hover"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedGroupEvents(
                              Array.isArray(g.events) ? g.events : [],
                            );
                            setSelectedGroupForEvents(g);
                            setShowEventsModal(true);
                          }}
                        >
                          {Array.isArray(g.events) ? g.events.length : 0}
                        </div>
                      ) : Array.isArray(g.events) ? (
                        g.events.length
                      ) : (
                        0
                      )}
                    </td> */}
                    <td>
                      {g?.adminEmails
                        ? g.adminEmails.split(",").map((email, index, arr) => (
                            <span key={index}>
                              <a href={`mailto:${email.trim()}`}>
                                {email.trim()}
                              </a>
                              {index < arr.length - 1 && ", "}
                            </span>
                          ))
                        : "-"}
                    </td>
                    <td>
                      {g.uniqueKey ? (
                        <div className="action-btns-2">
                          <a href={`${baseUrl}${g.uniqueKey}?${g.groupName.replace(" ","_")}`} target="_blank">
                            {baseUrl}
                            {g.uniqueKey}?{g.groupName.replace(" ","_")}
                          </a>
                          <Button
                            title="Copy URL"
                            onClick={() => {
                              navigator.clipboard.writeText(
                                `${baseUrl}${g.uniqueKey}?${g.groupName.replace(" ","_")}`,
                              );
                              NotificationManager.success(
                                "URL copied to clipboard",
                              );
                            }}
                            className="copy-btn"
                          >
                            <FontAwesomeIcon icon={faCopy} />
                          </Button>
                        </div>
                      ) : (
                        "-"
                      )}
                    </td>
                    <td
                      style={{
                        display: "flex",
                        gap: 8,
                        alignItems: "center",
                        justifyContent: "flex-end",
                      }}
                    >
                      {g.eventCount === 0 && (
                        <>
                          <Button onClick={() => handleEditGroup(g)}>
                            Edit
                          </Button>
                          <Button
                            onClick={(e) => handleDelete(e, g)}
                            type="danger"
                          >
                            Delete
                          </Button>
                        </>
                      )}
                      <Button onClick={() => handleOpenAdminModal(g)}>
                        Add Admin
                      </Button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={7} style={{ textAlign: "center" }}>
                    No groups found.
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </div>
      </div>

      <Modal
        isOpen={showModal}
        closeModal={() => {
          setShowModal(false);
          setIsEditing(false);
          setEditingGroupId(null);
          setNewGroupName("");
          setNewGroupLocation("");
          setNewGroupActive(true);
        }}
        className="group-modal"
      >
        <h2>{isEditing ? "Edit Group" : "Create Group"}</h2>
        <div className="data">
          <div className="row">
            <div className="add">
              <label>Group Name</label>
              <Input
                value={newGroupName}
                maxLength={50}
                onChange={(e) => setNewGroupName(e.target.value)}
              />
            </div>
            <div className="add">
              <label>Group Location</label>
              <Input
                value={newGroupLocation}
                maxLength={50}
                onChange={(e) => setNewGroupLocation(e.target.value)}
              />
            </div>
            <div className="add">
              <label>Is Active</label>
              <ToggleSwitch
                checked={newGroupActive}
                onChange={(e) => setNewGroupActive(e.target.checked)}
              />
            </div>
          </div>
        </div>
        <Button onClick={handleCreateGroup}>
          {isEditing ? "Update" : "Submit"}
        </Button>
      </Modal>

      {/* Group Admin Modal */}
      <Modal
        isOpen={showAdminModal}
        closeModal={() => {
          setShowAdminModal(false);
          setSelectedGroupForAdmin(null);
          setGroupAdmins([]);
        }}
        className="group-modal"
      >
        <h2>Group Admins - {selectedGroupForAdmin?.groupName || ""}</h2>
        <div className="data">
          <div className="row">
            <div className="add" style={{ flex: 1 }}>
              <label>Admin Email</label>
              <Input
                value={newAdminEmail}
                onChange={(e) => setNewAdminEmail(e.target.value)}
              />
            </div>
            <div className="add" style={{ width: 140 }}>
              <label>Is Active</label>
              <ToggleSwitch
                checked={newAdminActive}
                onChange={(e) => setNewAdminActive(e.target.checked)}
              />
            </div>
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <Button onClick={handleCreateGroupAdmin}>Add Admin</Button>
            <Button
              type="primary"
              onClick={() => {
                setShowAdminModal(false);
                setSelectedGroupForAdmin(null);
                setGroupAdmins([]);
              }}
            >
              Close
            </Button>
          </div>
          <hr />
          <div>
            <h4>Existing Admins</h4>
            {Array.isArray(groupAdmins) && groupAdmins.length > 0 ? (
              <table style={{ width: "100%" }}>
                <thead>
                  <tr>
                    <th>Email</th>
                    <th>Active</th>
                  </tr>
                </thead>
                <tbody>
                  {groupAdmins.map((a) => (
                    <tr key={a.groupAdminId}>
                      <td>{a.groupAdminEmail}</td>
                      <td>
                        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                          <ToggleSwitch
                            checked={!!a.isActive}
                            onChange={() => handleToggleAdminStatus(a)}
                          />
                          <Button type="danger" onClick={() => handleConfirmDeleteAdmin(a)}>Delete</Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <p className="no-groups">No admins found for this group.</p>
            )}
          </div>
        </div>
      </Modal>

      {/* Events Modal */}
      <Modal
        isOpen={showEventsModal}
        closeModal={() => {
          setShowEventsModal(false);
          setSelectedGroupEvents([]);
          setSelectedGroupForEvents(null);
        }}
        className="modal-2 events-modal"
      >
        <h4>Events</h4>
        <div className="event-list">
          {Array.isArray(selectedGroupEvents) &&
          selectedGroupEvents.length > 0 ? (
            <ul>
              {selectedGroupEvents.map((e, i) => (
                <li key={`${i}`}>
                  <a
                    className="v-link"
                    href={e.eventId ? `/form-group/${e.eventId}` : "#"}
                  >
                    {e.pageName || JSON.stringify(e)}
                  </a>
                </li>
              ))}
            </ul>
          ) : (
            <p>No events for this group.</p>
          )}
        </div>
        <div className="v-modal-button-wrapper">
          <Button
            onClick={() => {
              setShowEventsModal(false);
              setSelectedGroupEvents([]);
              setSelectedGroupForEvents(null);
            }}
          >
            Close
          </Button>
        </div>
      </Modal>

      <Modal
        isOpen={deleteModalOpen}
        closeModal={closeDeleteModal}
        className="delete-modal"
      >
        <div className="circle">!</div>
        <h4>Are you sure you want to remove this group?</h4>
        <div className="modal-buttons">
          <Button onClick={handleDeleteGroup}>Yes, Remove it</Button>
          <Button onClick={closeDeleteModal} type="primary">
            Cancel
          </Button>
        </div>
      </Modal>

      
    </MainA>
  );
};

export default Group;
